import { useCallback } from "react";
import {
  useCustomMutation,
  useInvalidate,
  useTranslate,
} from "@refinedev/core";
import { notification } from "antd";
import { mapSlideToTemplateState } from "../helpers/mapTemplateSlidesToTemplateState";
import { mapTemplateStateToServerTemplate } from "../helpers/mapTemplateStateToServerTemplate";
import { mergeThemeWithTemplate } from "../helpers/mergeThemeWithTemplate";
import {
  useTemplateEditorStore,
  useTemplateEditorStoreApi,
} from "../store/useTemplateEditorStore";
import { useTemplateData } from "./useTemplateData";
import { useSaveTemplate } from "./useSaveTemplate";
import type {
  LayoutTemplateResponse,
  ThemeResponse,
} from "../../themeEditor/hooks/useThemeApi";
import { TemplateState } from "../types";

export const useApplyTheme = () => {
  const {
    projectId,
    instanceId,
    organizationId,
    storyboardWithSlidesData: { slides },
    // Neither the per-slide template PATCH loop nor the theme_id settings
    // PATCH touches an asset, so BE never auto-marks the media here — flag it
    // ourselves once the theme is actually applied. The publication is left
    // alone: nothing has been regenerated yet, so nothing live is actually out
    // of date — BE marks the publication once the regenerated media lands
    // (see MED-6969).
    mediaGenerationSettingsData: {
      mediaAspectRatio,
      markMediaOutOfDate,
      mediaGenerationSettings,
    },
    brandKitData,
    templatesData: { presenter: initialPresenter },
    logoRatio,
    presenterRatio,
  } = useTemplateData();

  const { mutateAsync: updateSettings } = useCustomMutation();
  const invalidate = useInvalidate();
  const t = useTranslate();
  const { updateTemplate } = useSaveTemplate(organizationId);
  const templateEditorStoreApi = useTemplateEditorStoreApi();

  const {
    templates,
    serverTemplates,
    slideNameToTemplateIdMap,
    setTemplate,
    setTemplateState,
    currentSlide,
  } = useTemplateEditorStore((state) => ({
    templates: state.templates,
    serverTemplates: state.serverTemplates,
    slideNameToTemplateIdMap: state.slideNameToTemplateIdMap,
    setTemplate: state.setTemplate,
    setTemplateState: state.setTemplateState,
    currentSlide: state.currentSlide,
  }));

  const applyTheme = useCallback(
    async (theme: ThemeResponse) => {
      if (!mediaAspectRatio) return;

      const commonAssets = {
        brandKitData: { logo: brandKitData.logo, audio: undefined },
        initialPresenter,
        presenterRatio,
        logoRatio,
        forceAdjustFont: true,
      };

      const previousTemplates = new Map<string, TemplateState>(templates);

      type SlideToSave = {
        slideId: string;
        mergedTemplate: TemplateState;
        themeLayout: LayoutTemplateResponse;
      };
      const slidesByTemplateId = new Map<string, SlideToSave[]>();

      let currentSlideTemplate: TemplateState | undefined;

      for (const [slideId, existingTemplate] of templates) {
        const slideType = existingTemplate.slideLayoutType;
        if (!slideType) continue;

        const themeLayout = theme.layouts[slideType];
        if (!themeLayout) continue;

        const themeState = mapSlideToTemplateState(
          {
            id: "",
            sprite_definitions: themeLayout.layout,
            name: slideId,
            avatarShot: existingTemplate.presenter.avatarShot,
            music: null,
            inputs: {},
          },
          slides,
          mediaAspectRatio,
          commonAssets
        );

        const slidePayload = slides.find((s) => s.uuid === slideId);
        const slideHasImageElement =
          !!slidePayload && "image_element" in slidePayload.placeholders;

        const mergedTemplate = mergeThemeWithTemplate(
          existingTemplate,
          themeState,
          slideHasImageElement
        );

        setTemplate(slideId, mergedTemplate);

        if (slideId === currentSlide) {
          currentSlideTemplate = mergedTemplate;
        }

        const templateId = slideNameToTemplateIdMap[slideId];
        if (!templateId) continue;
        const group = slidesByTemplateId.get(templateId) ?? [];
        group.push({ slideId, mergedTemplate, themeLayout });
        slidesByTemplateId.set(templateId, group);
      }

      if (currentSlideTemplate) {
        setTemplateState(currentSlideTemplate);
      }

      // Build server payloads per template.
      const validSlideUuids = new Set(slides.map((s) => s.uuid));
      const saveOps: Promise<void>[] = [];
      for (const [templateId, group] of slidesByTemplateId) {
        let accumulated = serverTemplates?.get(templateId);
        if (!accumulated) continue;

        const isTransitionSlide = mediaGenerationSettings?.chapters.some(
          (c) => c.transition_template_id === templateId
        );

        for (const { slideId, mergedTemplate, themeLayout } of group) {
          const mapped = mapTemplateStateToServerTemplate(
            slideId,
            mergedTemplate,
            accumulated,
            mediaAspectRatio,
            validSlideUuids,
            !!isTransitionSlide,
            themeLayout.layout
          );
          if (!mapped) continue;
          accumulated = mapped;
        }
        saveOps.push(updateTemplate(templateId, accumulated));
      }

      try {
        await Promise.all(saveOps);

        await updateSettings({
          url: `media/projects/${projectId}/instances/${instanceId}/settings`,
          method: "patch",
          values: { theme_id: theme.id },
        });

        await invalidate({
          resource: "media/projects",
          id: projectId,
          invalidates: ["detail"],
        });
      } catch {
        previousTemplates.forEach((tpl, slideId) => {
          setTemplate(slideId, tpl);
        });
        // The user may have switched slides while the apply was in flight:
        // restore the design of the slide selected now, not the one selected
        // when the apply started.
        const selectedSlide = templateEditorStoreApi.getState().currentSlide;
        const selectedSlideTemplate = selectedSlide
          ? previousTemplates.get(selectedSlide)
          : undefined;
        if (selectedSlideTemplate) {
          setTemplateState(selectedSlideTemplate);
        }
        notification.error({
          message: t("src.App.error"),
          description: t("media.themeEditor.applyError"),
        });
        return;
      }

      // The theme is already applied at this point — a failure here must
      // never trigger the revert above (there's also no "unmark out of
      // date" action to undo it with), so this is deliberately outside the
      // try/catch that guards the apply itself.
      await markMediaOutOfDate();
    },
    [
      brandKitData.logo,
      initialPresenter,
      logoRatio,
      presenterRatio,
      mediaAspectRatio,
      mediaGenerationSettings?.chapters,
      slides,
      templates,
      serverTemplates,
      slideNameToTemplateIdMap,
      setTemplate,
      setTemplateState,
      currentSlide,
      templateEditorStoreApi,
      updateTemplate,
      updateSettings,
      invalidate,
      projectId,
      instanceId,
      markMediaOutOfDate,
      t,
    ]
  );

  return { applyTheme };
};
